import Table, { ColumnProps } from './Table'
import { always, once } from 'ramda'
import { getAbilityPromise } from '../defineAbility'
import { Badge, Themes } from './Badge'
import { Report, deleteReport, createReport } from '~/api/reports'
import axios from 'axios'
import { router } from '~/router'
import { useReports } from './useReports'

/**
 * @typedef TableReportsComputed
 * @property { Report[] } reports
 * @property { boolean } isReportsLoading
 */

/** @typedef { import('vue/types/vue').Vue } Vue */
/** @typedef { import('./Table').ColumnPropsType<import('~/api/reports').Report> } ColumnPropsReport */
/** @typedef { import('vue/types/options').FunctionalComponentOptions<ColumnPropsReport, typeof ColumnProps> } ColumnReport */
/** @typedef { import('vue/types/options').ThisTypedComponentOptionsWithRecordProps<Vue, {}, {}, TableReportsComputed, {}> } TableReportsComponent */

/** @type { import('@casl/ability').Ability } */
let ability

getAbilityPromise.then(newAbility => ability = newAbility)

/** @enum */
const Status = {
	Unresolved: 'Ожидание',
	Rejected: 'Отклонён',
	Confirmed: 'Подтверждён',
}

const BadgeOptions = {
	[Status.Unresolved]: Themes.Grey,
	[Status.Confirmed]: Themes.Blue,
	[Status.Rejected]: Themes.Red,
}

const ActionRecreate = {
	render: {
		name: 'ActionRecreate',
		props: ColumnProps,
		data: () => ({
			loading: false,
		}),
		computed: {
			isRejected: (vm) => vm.record[Report.Rejected],
		},
		methods: {
			recreateReport() {
				this.loading = true

				createReport({ id: this.record[Report.Id] })
					.then((response) => {
						if (!response.status) return

						useReports().mutate()
					})
					.finally(() => this.loading = false)
			},
		},
		render(h) {
			return this.isRejected ? (
				<button
					class="button button_theme_secondary button_size_s"
					onClick={ this.recreateReport }
					disabled={ this.loading }
				>
					Пересоздать
				</button>
			) : null
		}
	},
}

const ActionDelete = {
	render: {
		name: 'ActionDelete',
		props: ColumnProps,
		data: () => ({
			loading: false,
		}),
		computed: {
			isConfirmed: (vm) => vm.record[Report.Confirmed],
		},
		methods: {
			deleteReport() {
				this.loading = true

				let source = new AbortController()

				this.$options.source?.abort?.()
				this.$options.source = source

				deleteReport(this.record[Report.Id])
					.then((response) => {
						if (!response.status) return

						useReports().data.splice(this.idx, 1)
						useReports().mutate()
					})
					.finally(() => this.loading = false)
			},
		},
		render(h) {
			return !this.isConfirmed ? (
				<button
					class="button button_theme_secondary-ghost button_size_s"
					onClick={ this.deleteReport }
					disabled={ this.loading }
				>
					Удалить
				</button>
			) : null
		}
	},
}

const ColumnName = {
	name: '#',
	dataIndex: Report.Name,
}

const ColumnLinks = {
	render: {
		name: 'ReportLink',
		functional: true,
		props: ColumnProps,
		render: (h, { props }) => (
			<span>
				<a
					class="request-edit-link"
					href={ `/api/Reports/get/?format=pdf&id=${props.record[Report.Id]}` }
					target="_blank"
					download
				>
					PDF
				</a>
				&nbsp;
				<a
					class="request-edit-link"
					href={ `/api/Reports/get/?format=xls&id=${props.record[Report.Id]}` }
					target="_blank"
					download
				>
					XLS
				</a>
			</span>
		),
	},
}

const ColumnServiceName = {
	name: 'Name SC',
	dataIndex: Report.ServiceName,
}

const ColumnStatus = {
	name: 'Status',
	dataIndex: Report.Status,
	render: {
		functional: true,
		name: 'ColumnStatus',
		render(h, { props }) {
			return (
				<Badge { ...{
					props: {
						text: props.value,
						...BadgeOptions[props.value],
					},
				}}/>
			)
		},
	},
}

const ColumnCreated = {
	name: 'Created',
	dataIndex: Report.Created,
}

const ColumnRejected = {
	name: 'Rejected',
	dataIndex: Report.Rejected,
}

const ColumnConfirmed = {
	name: 'Confirmed',
	dataIndex: Report.Confirmed,
}

const getColumns = () => {
	const columns = []

	columns.push(ColumnName, ColumnLinks)

	if (ability.can('view', 'Reports', Report.ServiceName))
		columns.push(ColumnServiceName)

	columns.push(ColumnStatus, ColumnCreated, ColumnRejected, ColumnConfirmed)

	if (ability.can('create', 'Reports'))
		columns.push(ActionRecreate)

	if (ability.can('delete', 'Reports'))
		columns.push(ActionDelete)

	return columns
}

/** @type {import('vue').FunctionalComponentOptions} */
const CustomTable = {
	name: 'CustomTable',
	functional: true,
	render: (h, { data, slots }) => (
		<div class="request-table">
			<table { ...data }>
				{ slots().default }
			</table>
		</div>
	),
}

/** @type {import('vue').FunctionalComponentOptions} */
const CustomHeaderRow = {
	name: 'CustomHeaderRow',
	functional: true,
	render: (h, { slots }) => (
		<tr class="request-table_header">
			{ slots().default }
		</tr>
	),
}

/** @type {TableReportsComponent} */
const TableReports = {
	name: 'TableReports',
	router,
	computed: {
		reports: () => useReports().data,
		isReportsLoading: () => useReports().isValidating,
	},
	created() {
		useReports().mutate()
	},
	render(h) {
		const columns = getColumns()

		return (
			<Table
				dataSource={ this.reports }
				columns={ columns }
				rowClassName={ always('name-test') }
				components={{
					table: CustomTable,
					header: {
						row: CustomHeaderRow,
					},
				}}
				loading={ this.isReportsLoading }
			/>
		)
	}
}

export default TableReports
